import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useApp } from "@/context/AppContext";

const LINKS = [
  { key: "home", id: "top" },
  { key: "properties", id: "strutture" },
  { key: "destinations", id: "destinazioni" },
  { key: "about", id: "chi-siamo" },
  { key: "book", id: "booking" },
  { key: "contact", id: "contatti" },
];

export default function Header() {
  const { t, lang, setLang, scrollToId } = useApp();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    if (window.location.pathname !== "/") { navigate("/"); setTimeout(() => scrollToId(id), 350); }
    else scrollToId(id);
    setOpen(false);
  };

  const textColor = scrolled ? "text-brand-black" : "text-white";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-brand-white/90 backdrop-blur-md shadow-sm border-b border-brand-ivory" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <button data-testid="logo-btn" onClick={() => go("top")} className={`text-left ${textColor}`}>
          <div className="font-heading text-xl md:text-2xl tracking-[0.2em] uppercase leading-none">Martelli Rentals</div>
          <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase opacity-70 mt-0.5">Italian Homes &amp; Hospitality</div>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.key}
              data-testid={`nav-${l.key}`}
              onClick={() => go(l.id)}
              className={`text-sm font-medium tracking-wide hover:text-brand-gold transition-colors ${textColor}`}
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <div className={`hidden sm:flex items-center text-xs font-medium ${textColor}`}>
            {["it", "en"].map((lc, i) => (
              <span key={lc} className="flex items-center">
                {i > 0 && <span className="opacity-40 mx-1">/</span>}
                <button
                  data-testid={`lang-${lc}`}
                  onClick={() => setLang(lc)}
                  className={`uppercase tracking-wide transition-opacity ${lang === lc ? "opacity-100 underline underline-offset-4" : "opacity-50 hover:opacity-90"}`}
                >
                  {lc}
                </button>
              </span>
            ))}
          </div>

          <button
            data-testid="header-book-btn"
            onClick={() => go("booking")}
            className="hidden md:inline-flex bg-brand-olive text-brand-white hover:bg-brand-black transition-colors rounded-full px-5 py-2.5 text-sm font-medium"
          >
            {t.nav.bookNow}
          </button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" aria-label="Apri menu" className={`lg:hidden p-2 ${textColor}`}>
                <Menu className="w-6 h-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-brand-white w-[300px] border-brand-ivory">
              <SheetTitle className="font-heading text-2xl tracking-widest uppercase text-brand-olive">Menu</SheetTitle>
              <nav className="mt-8 flex flex-col gap-1">
                {LINKS.map((l) => (
                  <button
                    key={l.key}
                    data-testid={`mobile-nav-${l.key}`}
                    onClick={() => go(l.id)}
                    className="text-left py-3 text-lg font-heading text-brand-black border-b border-brand-ivory hover:text-brand-gold transition-colors"
                  >
                    {t.nav[l.key]}
                  </button>
                ))}
              </nav>
              <div className="mt-6 flex items-center gap-3 text-sm">
                {["it", "en"].map((lc) => (
                  <button key={lc} data-testid={`mobile-lang-${lc}`} onClick={() => setLang(lc)}
                    className={`uppercase px-3 py-1 rounded-full border ${lang === lc ? "bg-brand-olive text-white border-brand-olive" : "border-brand-ivory text-brand-brown"}`}>
                    {lc}
                  </button>
                ))}
              </div>
              <button data-testid="mobile-book-btn" onClick={() => go("booking")} className="mt-6 w-full bg-brand-olive text-white rounded-full py-3 font-medium">
                {t.nav.bookNow}
              </button>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
